import { Clock } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import { MarketShell } from '../../design-system/components/shells';
import { Button, Empty, PageHeader } from '../../design-system/components/desktop';
import { ConsoleStack } from '../../design-system/patterns/console';

/**
 * Shown on /dashboard when a user has registered as a seller but their
 * application is still pending admin approval (role hasn't flipped to "seller").
 * Sets the expectation that approval is coming and the dashboard is worth
 * returning to, while giving them somewhere to go in the meantime.
 *
 * It is a STATE OF `/dashboard`, so it keeps the dashboard's chrome rather than
 * replacing the whole viewport: the user is in the right place, there is just
 * nothing here yet. `Empty`'s default tone is exactly the "nothing here yet"
 * one, and the way out is the marketplace.
 *
 * WHAT DIED: `SellerPendingApproval.css`, the `SellerDashboard/Layout` wrapper
 * (Tailwind classes against a build with no Tailwind in it, so the header it
 * claimed to fix was never actually positioned), and an em dash in the copy,
 * which is not allowed anywhere in this product.
 */
const SellerPendingApproval = ({ storeName }) => {
  const navigate = useNavigate();

  return (
    <MarketShell
      title="Dashboard"
      breadcrumbs={[{ label: 'Dashboard' }]}
      showSearch={false}
      activeNav="dashboard"
    >
      <ConsoleStack>
        <PageHeader title="Dashboard" sub="Your seller account is under review." />
        <Empty
          icon={Clock}
          illustration="awaiting-approval"
          title="Your seller account is under review"
          text={`${
            storeName ? `Thanks for registering "${storeName}". ` : 'Thanks for registering. '
          }An administrator will approve your account soon. Check back here to set up your store and start listing cards.`}
          detail="We will email you as soon as you are verified."
          actions={
            <Button variant="primary" onClick={() => navigate('/marketplace')}>
              Browse the marketplace
            </Button>
          }
        />
      </ConsoleStack>
    </MarketShell>
  );
};

export default SellerPendingApproval;
